/**
 * @file pages/LauncherPage.js
 * @description Butler Core — the post-login tile page ("the front door's foyer").
 *
 * One login → this grid of every Butler app. Live apps open on tap; the rest
 * are shown as "coming soon" so the suite's shape is always visible.
 *
 * Icons are inline SVGs (stroke style, currentColor) — emoji rendered
 * washed-out/wrong on Windows (the On-Call pager looked like a calculator).
 * Entitlement gating is deferred: every tile is visible; availability is
 * informational for now. TWO exceptions, both server-truth-coupled probes that
 * live below the APPS list:
 *   - the Overwatch tile (2026-08-13), hidden unless the server confirms this
 *     user is an Overwatch owner;
 *   - the HR Butler tile (2026-08-14), hidden unless the server confirms this
 *     user is an owner or company admin.
 * Both fail closed and fail INVISIBLE — a greyed-out tile still advertises a
 * door, so a tile this user cannot open is never drawn at all.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, Button, useToast } from "../design-system";

/* ---- Inline icon set (24x24 viewBox, stroke = currentColor) ---- */
const S = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
const ICONS = {
  water:      <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M12 2.5S5.5 10 5.5 14.5a6.5 6.5 0 0 0 13 0C18.5 10 12 2.5 12 2.5Z" /><path d="M9.3 14.2l1.8 1.8 3.4-3.4" /></svg>,
  inspection: <svg width="28" height="28" viewBox="0 0 24 24" {...S}><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.5 15.5 21 21" /><path d="M8 10.5h5M10.5 8v5" /></svg>,
  well:       <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M12 3c3.5 4.2 6 7.4 6 10.2A6 6 0 0 1 6 13.2C6 10.4 8.5 7.2 12 3Z" /><path d="M9.5 13.5a2.6 2.6 0 0 0 2.5 2.7" /></svg>,
  oncall:     <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M18 9a6 6 0 0 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9Z" /><path d="M10 19a2.2 2.2 0 0 0 4 0" /></svg>,
  schedule:   <svg width="28" height="28" viewBox="0 0 24 24" {...S}><rect x="2" y="4.5" width="9" height="3.5" rx="1" /><rect x="13" y="4.5" width="9" height="3.5" rx="1" /><rect x="6" y="11" width="12" height="3.5" rx="1" /><rect x="2" y="17.5" width="7" height="3.5" rx="1" /><rect x="15" y="17.5" width="7" height="3.5" rx="1" /></svg>,
  note:       <svg width="28" height="28" viewBox="0 0 24 24" {...S}><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>,
  dispatch:   <svg width="28" height="28" viewBox="0 0 24 24" {...S}><rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 9.5h17M8 3v4M16 3v4M8 13.5h3M8 16.8h6" /></svg>,
  invoice:    <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M6 3h12v18l-2-1.4L14 21l-2-1.4L10 21l-2-1.4L6 21V3Z" /><path d="M9.5 8h5M9.5 11.5h5M9.5 15h3" /></svg>,
  payment:    <svg width="28" height="28" viewBox="0 0 24 24" {...S}><rect x="3" y="6" width="18" height="13" rx="2.5" /><path d="M3 10.2h18M7 15h4" /></svg>,
  customer:   <svg width="28" height="28" viewBox="0 0 24 24" {...S}><circle cx="9" cy="8.5" r="3.2" /><path d="M3.5 19.5c0-3 2.5-4.8 5.5-4.8s5.5 1.8 5.5 4.8" /><circle cx="16.8" cy="9.5" r="2.4" /><path d="M16.5 14.6c2.4.2 4.2 1.7 4.2 4" /></svg>,
  field:      <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M12 21s-6.5-6.2-6.5-11A6.5 6.5 0 0 1 18.5 10c0 4.8-6.5 11-6.5 11Z" /><circle cx="12" cy="10" r="2.4" /></svg>,
  analytics:  <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M4 20h16" /><path d="M6.5 20v-6M11 20V8.5M15.5 20v-9M20 20V5" /></svg>,
  drive:      <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M7 18a4.2 4.2 0 0 1-.5-8.4 5.6 5.6 0 0 1 11-1A4 4 0 0 1 17 18H7Z" /></svg>,
  comms:      <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5Z" /><path d="M8.5 11h.01M12 11h.01M15.5 11h.01" /></svg>,
  busbook:    <svg width="28" height="28" viewBox="0 0 24 24" {...S}><rect x="3" y="4" width="18" height="12.5" rx="2" /><path d="M7 13v-3M11 13V8.5M15 13v-2M12 16.5V20M8.5 20h7" /></svg>,
  safety:     <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M12 2L3 6v6c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V6l-9-4z" /><path d="M9 12l2 2 4-4" /></svg>,
  manual:     <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>,
  hr:         <svg width="28" height="28" viewBox="0 0 24 24" {...S}><circle cx="9" cy="7.5" r="3.4" /><path d="M2.8 20c0-3.2 2.8-5.2 6.2-5.2s6.2 2 6.2 5.2" /><path d="M17.5 4.2a3.4 3.4 0 0 1 0 6.6M18 14.9c2.1.5 3.4 2.2 3.4 4.6" /></svg>,
  overwatch:  <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.6" /></svg>,
  truck:      <svg width="28" height="28" viewBox="0 0 24 24" {...S}><path d="M2.5 6.5h11v9h-11z" /><path d="M13.5 9.5h3.7l3.3 3.3v2.7h-7z" /><circle cx="6.8" cy="17.6" r="1.9" /><circle cx="17" cy="17.6" r="1.9" /></svg>,
  tasks:      <svg width="28" height="28" viewBox="0 0 24 24" {...S}><rect x="4.5" y="4" width="15" height="17" rx="2.5" /><path d="M9 4V2.8h6V4" /><path d="M8.5 10.5l2 2 4-4M8.5 16.5h7" /></svg>,
};

const APPS = [
  // The Truck App rides FIRST: on a phone the grid is one column, so list
  // order is screen position, and a tech opens this standing in a driveway.
  // href is RELATIVE on purpose (same-origin field app), never a hardcoded host.
  { id: "truck",      name: "Truck App",         desc: "Today's stops, directions, and done — the app that rides in the truck.", href: "/api/schedule/field/" },
  { id: "inspection", name: "Inspection Butler", desc: "Inspections that practically write their own reports.", href: "https://butlercore.com/report-engine/editor" },
  { id: "well",       name: "Well & Septic Butler", desc: "The old-timer in your pocket — sizes, diagnoses, sees.",  to: "/well" },
  { id: "water",      name: "WaterTestingNow",   desc: "Make the water test reports — with a treatment recommender built right in.", href: "https://butlercore.com/water/" },
  // On-Call opens at the butlercore door: same-domain session carries over
  // (no second login). Door-auth PROVEN 2026-06-05 via disposable-account probe
  // (/api/oncall/me -> 200 with a butlercore.com session cookie).
  { id: "oncall",     name: "On-Call Butler",    desc: "Who's on tonight? It already knows.",      href: "https://butlercore.com/oncall/" },
  { id: "schedule",   name: "Schedule Butler",   desc: "We speak what your employees speak — scheduling + clock in/out in 17 languages.", href: "https://butlercore.com/schedule/" },
  { id: "safety",    name: "Safety Butler",   desc: "Free for every field worker — SOS, man-down detection, check-in timers, and confined space protection.", href: "https://butlercore.com/safety/", badge: "FREE" },
  // HR Butler — the company people file. The tile is OWNER/ADMIN-ONLY as of
  // 2026-08-14: it is filtered out of the grid entirely unless the server says
  // this user passes the same gate the module itself sits behind. Field crew
  // never see it. (The module would still show them a polite "wrong door" page
  // if they typed the address, but a tile nobody can open is clutter that
  // advertises a door — so it simply is not drawn. See HR_PROBE below.)
  { id: "hr",         name: "HR Butler",         desc: "Everybody who works here, in one place — add them, change what they do, keep their licences.", href: "https://butlercore.com/hr/" },
  { id: "comms",      name: "Comms Butler",      desc: "Every call & text captured — flags the ones going sideways.", href: "https://butlercore.com/comms/" },
  // Note Butler + BusBook open via the SSO handoff: the hub mints a one-time
  // token and the app's /auth/callback redeems it back-channel — one tap, no
  // second password. (BusBook is invite-only: non-approved users get a polite 403.)
  { id: "note",       name: "Note Butler",       desc: "Remembers every call so you don't have to.", href: "https://butlercore.com/note/" },
  { id: "manual",     name: "Manual Butler",     desc: "The Butler Suite instruction manual — searchable, mobile-first, always current.", href: "https://butlercore.com/manual/" },
  { id: "busbook",    name: "Bus Book Butler",   desc: "The watchman — every Butler site watched, every outage reported.", href: "https://butlercore.com/app/api/auth/sso/authorize?redirect_uri=https%3A%2F%2Fbusbookbutler.com" },
  { id: "dispatch",   name: "Dispatch Butler",   desc: "The dispatch board that runs itself.", href: "https://butlercore.com/dispatch" },
  // Tasks Butler - promoted to live 2026-08-22 (greenline lane 1). Opens at
  // the butlercore door exactly like /note/ and /hr/: the module lives inside
  // the IB API, so the butlercore.com session cookie rides along - no second
  // login. TO UNDO: delete this tile object; nothing else depends on it.
  { id: "tasks",      name: "Tasks Butler",      desc: "The shared job list — what needs doing, who it’s for, when it’s wanted by.", href: "https://butlercore.com/tasks/" },
  // Owner's cross-company god-view. The tile is OWNER-ONLY as of 2026-08-13:
  // it is filtered out of the grid entirely unless the server says this user
  // passes the real Overwatch gate. See OVERWATCH_PROBE below — the browser
  // never holds a copy of the allowlist, it just asks.
  { id: "overwatch",  name: "Overwatch",         desc: "Every login, every company, one screen — owner's-eye view.", href: "https://butlercore.com/api/overwatch/ui" },
  { id: "invoice",    name: "Invoice Butler",    desc: "Bill the job before you're back in the truck." },
  { id: "payment",    name: "Payment Butler",    desc: "Get paid without chasing anybody." },
  { id: "customer",   name: "Customer Butler",   desc: "Every customer, every property, every history." },
  { id: "field",      name: "Field Butler",      desc: "Where the trucks are — and where they've been." },
  { id: "analytics",  name: "Analytics Butler",  desc: "Know which jobs actually make you money." },
  { id: "drive",      name: "Drive Butler",      desc: "Every file on every device. No more shoebox." },
];

/* ---- Overwatch tile: the truth-coupled owner gate (2026-08-13) -------------
 * The Overwatch console has always been owner-only on the server — an explicit
 * OVERWATCH_OWNERS env allowlist, not a grantable role. The TILE, though, was
 * advertised to every signed-in employee, which told the whole company a door
 * exists that only James can walk through.
 *
 * We do NOT copy the allowlist into the browser bundle — a second copy of a
 * secret is a second thing that can drift out of step with the first. Instead
 * the page ASKS the same gate the console itself sits behind:
 *
 *   GET /api/overwatch/whoami   →  authenticate + ownerOnly  →  200 { owner:true }
 *
 * whoami is the lightest owner-gated GET in modules/overwatch/routes.js — it
 * reads no database and returns a few bytes of JSON — and it lives BELOW the
 * `router.use(authenticate, ownerOnly)` line, so a 200 back is proof that this
 * exact browser, with this exact cookie, would be let into the console. Any
 * other answer — 401, 403, 500, DNS down, offline — means no tile at all.
 *
 * Fail closed and fail INVISIBLE: the tile starts absent and is only ever added,
 * never greyed out. A greyed tile still advertises the door.
 *
 * The answer is cached in sessionStorage so the probe fires once per browser
 * session, not once per render (and clears itself when the tab closes, so a
 * shared truck laptop can't inherit the last person's answer).
 */
const OVERWATCH_API = process.env.REACT_APP_API_URL || "/api";
const OVERWATCH_PROBE_URL = OVERWATCH_API + "/overwatch/whoami";
const OVERWATCH_CACHE_KEY = "bc.overwatch.owner";
let overwatchProbeInFlight = null; // one probe per page load, shared by every caller

function probeOverwatchOwner() {
  try {
    const remembered = window.sessionStorage.getItem(OVERWATCH_CACHE_KEY);
    if (remembered === "1") return Promise.resolve(true);
    if (remembered === "0") return Promise.resolve(false);
  } catch (e) {
    /* private mode / storage disabled — fall through and just ask the server */
  }
  if (overwatchProbeInFlight) return overwatchProbeInFlight;
  overwatchProbeInFlight = fetch(OVERWATCH_PROBE_URL, {
    method: "GET",
    credentials: "include",       // the login cookie is HttpOnly; the browser must send it
    headers: { Accept: "application/json" },
    cache: "no-store",
  })
    .then((res) => res.status === 200)
    .catch(() => false)           // network trouble is not permission — fail closed
    .then((isOwner) => {
      try { window.sessionStorage.setItem(OVERWATCH_CACHE_KEY, isOwner ? "1" : "0"); } catch (e) { /* ignore */ }
      return isOwner;
    });
  return overwatchProbeInFlight;
}

/* ---- HR Butler tile: the same truth-coupled gate, one job title down --------
 * (2026-08-14, promoting HR Butler to live.)
 *
 * HR Butler is owner/admin-only on the server: modules/hr/routes.js puts
 * `router.use(authenticate)` and `router.use(requireRole('admin',
 * 'company_admin', 'super_admin'))` above every JSON route, and the HTML shell
 * hands a field-crew member a polite 403 "wrong door" page instead.
 *
 * The tile follows the Overwatch precedent above rather than reading a role out
 * of the login context, and for the same reason: the browser's idea of your job
 * title is a COPY, and a copy can drift from the gate that actually decides. So
 * the page asks the gate itself:
 *
 *   GET /api/hr/me   →  authenticate + requireRole(...)  →  200 { me: {...} }
 *
 * /me is the lightest admin-gated GET in the module — it touches no database,
 * it just echoes the signed-in admin back — and it sits BELOW both middleware
 * lines, so a 200 is proof this exact browser, with this exact cookie, would be
 * let into HR Butler. Anything else (401, 403, 500, offline) means no tile.
 *
 * Fail closed, fail invisible, and cache the answer in sessionStorage so the
 * probe fires once per browser session and clears itself when the tab closes —
 * a shared truck laptop must not inherit the last person's answer.
 */
const HR_PROBE_URL = OVERWATCH_API + "/hr/me";
const HR_CACHE_KEY = "bc.hr.admin";
let hrProbeInFlight = null; // one probe per page load, shared by every caller

function probeHrAdmin() {
  try {
    const remembered = window.sessionStorage.getItem(HR_CACHE_KEY);
    if (remembered === "1") return Promise.resolve(true);
    if (remembered === "0") return Promise.resolve(false);
  } catch (e) {
    /* private mode / storage disabled — fall through and just ask the server */
  }
  if (hrProbeInFlight) return hrProbeInFlight;
  hrProbeInFlight = fetch(HR_PROBE_URL, {
    method: "GET",
    credentials: "include",       // the login cookie is HttpOnly; the browser must send it
    headers: { Accept: "application/json" },
    cache: "no-store",
  })
    .then((res) => res.status === 200)
    .catch(() => false)           // network trouble is not permission — fail closed
    .then((isAdmin) => {
      try { window.sessionStorage.setItem(HR_CACHE_KEY, isAdmin ? "1" : "0"); } catch (e) { /* ignore */ }
      return isAdmin;
    });
  return hrProbeInFlight;
}

export default function LauncherPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const toast = useToast();
  const firstName =
    (auth.user && (auth.user.firstName || (auth.user.name || "").split(" ")[0])) || "";

  // Overwatch tile visibility. Starts false — the grid renders WITHOUT the tile
  // and only grows it if the server confirms ownership, so there is never a
  // flash of a door that isn't yours.
  const [isOverwatchOwner, setIsOverwatchOwner] = useState(false);
  useEffect(() => {
    let mounted = true;
    probeOverwatchOwner().then((owner) => {
      if (mounted && owner) setIsOverwatchOwner(true);
    });
    return () => { mounted = false; };
  }, []);

  // HR Butler tile visibility — same shape as Overwatch above: starts false, so
  // the grid renders WITHOUT the tile and only grows it once the server agrees.
  const [isHrAdmin, setIsHrAdmin] = useState(false);
  useEffect(() => {
    let mounted = true;
    probeHrAdmin().then((admin) => {
      if (mounted && admin) setIsHrAdmin(true);
    });
    return () => { mounted = false; };
  }, []);

  const visibleApps = APPS.filter((a) => {
    if (a.id === "overwatch") return isOverwatchOwner;
    if (a.id === "hr") return isHrAdmin;
    return true;
  });

  // Derive the live/bench counts from what's actually ON SCREEN so the footer
  // can never drift again (and never counts a tile this user cannot see).
  const liveCount = visibleApps.filter((a) => a.to || a.href).length;
  const benchCount = visibleApps.length - liveCount;

  const open = (app) => {
    if (app.to) navigate(app.to);
    else if (app.href) window.location.assign(app.href);
  };

  return (
    <div style={{ maxWidth: 980, width: "100%", margin: "0 auto", padding: "var(--space-4) var(--space-3) var(--space-10)" }}>
      {/* James's red-circle placement (2026-07-09): the Mission Control door
          sits top-right, beside the welcome line, above the tile grid. */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-3)", flexWrap: "wrap", margin: "0 0 var(--space-1)" }}>
        <h1 style={{ fontSize: "var(--text-heading-lg, 1.6rem)", fontWeight: "var(--weight-bold)", margin: 0 }}>
          {firstName ? `Welcome back, ${firstName}.` : "Welcome back."}
        </h1>
        <a
          href="https://butlercore.com/mission-control"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "var(--color-primary, #0b66c3)", color: "#fff",
            padding: "10px 16px", borderRadius: 12, textDecoration: "none",
            fontWeight: "var(--weight-semibold, 600)", fontSize: "var(--text-body-sm, .95rem)",
            boxShadow: "0 2px 8px rgba(11,102,195,.35)", whiteSpace: "nowrap",
          }}
        >
          <span aria-hidden="true">{"\uD83D\uDE80"}</span> Mission Control
        </a>
      </div>
      <p style={{ color: "var(--text-secondary)", margin: "0 0 var(--space-4)", lineHeight: 1.5 }}>
        Everything's where you left it. What are we getting done today?
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: "var(--space-3)" }}>
        {visibleApps.map((app) => {
          const live = Boolean(app.to || app.href);
          return (
            <Card
              key={app.id}
              variant={live ? "interactive" : "default"}
              padding="md"
              onClick={live ? () => open(app) : undefined}
              aria-label={live ? `Open ${app.name}` : `${app.name} — coming soon`}
              style={live ? undefined : { border: "1.5px dashed var(--border-default, #d9d9de)" }}
            >
              <div style={{ opacity: live ? 1 : 0.6 }}>
                <div style={{ color: live ? "var(--color-primary)" : "var(--text-secondary)", marginBottom: "var(--space-2)" }}>
                  {ICONS[app.id]}
                </div>
                <div style={{ fontWeight: "var(--weight-semibold)", marginBottom: 2 }}>{app.name}</div>
                <div style={{ color: "var(--text-secondary)", fontSize: "var(--text-body-sm, 0.85rem)", lineHeight: 1.4, marginBottom: "var(--space-2)" }}>
                  {app.desc}
                </div>
                <span
                  style={{
                    display: "inline-block",
                    fontSize: "0.72rem",
                    fontWeight: "var(--weight-semibold)",
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: live ? "var(--color-success-soft, #e6f6ec)" : "var(--surface-secondary, #f1f1f3)",
                    color: live ? "var(--color-success, #1a7f37)" : "var(--text-secondary)",
                  }}
                >
                  {live ? "Open" : "Coming soon"}
                </span>
              </div>
            </Card>
          );
        })}
      </div>

      <p style={{ textAlign: "center", color: "var(--text-secondary)", fontSize: "var(--text-body-sm, 0.85rem)", marginTop: "var(--space-6)" }}>
        {liveCount} live today — {benchCount} more on the bench, and the bench is busy. 🛠️
      </p>
    </div>
  );
}
